/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';

import {
  Asset,
  AssetOption,
  Ducks,
  EntryPoint,
  Folder,
  OptionUtils,
  ProductPriceStatus,
  PurchaseOptionType,
  NumberUtils,
  FolderPurchaseOption,
} from '@wit/vtv-x-foundation-lib';

import RouterUtils from 'services/utils/RouterUtils';
import translate from 'services/i18n/translate';
import { ActionButton, AssetActionButton } from 'types/components/ActionButtonsGroup';

import { Hero, Modal, Pin, Rail as RailComponent, Spinner } from 'ui/components';
import { useDispatch, useSelector } from 'react-redux';
import { useLocationWithState } from 'hooks/useLocationWithState';
import { ActionCreators, FolderInfoContext, ParentContext, Selectors } from 'store/ducks/folderInfo';
import MainSection from 'types/MainSection';
import { useParams } from 'react-router';
import { StoreState } from 'store/store';
import * as ManagePin from 'store/ducks/settings/managePin';
import { Rail } from 'types/Rail';
import { RailBackgroundImageName } from 'types/components/ColdStart';
import ColdStartRail from 'ui/components/Rail/ColdStartRail';
import { useHistoryWithState } from 'hooks/useHistoryWithState';
import { PrivateRoutePath } from 'routes/paths';
import { addErrorEventByEntryPoint } from 'store/ducks/analytics/systemEvent';
import MoreInfo from './MoreInfo';
import { useRouter } from '../../../hooks/useRouter';

enum ActionButtonKey {
  MORE_INFO = 'folderBtnMore',
}

interface PathParams {
  assetId: string;
  entryPoint?: EntryPoint;
}

interface LocationState {
  entryAsset: Folder;
  parentContext?: ParentContext;
}

const getHeroActionButtons = (
  folder?: Folder,
  buttonsHandler?: (button: ActionButtonKey) => void
): ActionButton[] | undefined => {
  if (!folder) {
    return undefined;
  }

  const heroActionButtons: ActionButton[] = [];

  if (folder.moreInfo) {
    heroActionButtons.push({
      className: 'opaque',
      text: translate['buttons.subscription.findOutMore'],
      key: ActionButtonKey.MORE_INFO,
      onClick: () => buttonsHandler && buttonsHandler(ActionButtonKey.MORE_INFO),
    });
  }

  return heroActionButtons;
};

const renderRails = (
  rails: Rail[],
  onCardClick: (assetClicked: Asset) => void,
  onHeaderClick: (railId: number | string, channelId?: number) => void
) => {
  return rails.map((rail) => {
    if (!rail.assets.length && !rail.loadingAssets) {
      return (
        <ColdStartRail
          railId={rail.id}
          className="no-background"
          railName={rail.title}
          railBackgroundImageName={RailBackgroundImageName.WATCH_NEXT}
          railDescription=""
        />
      );
    }
    return (
      <RailComponent
        railId={rail.id}
        // menuItemId => don't send menu item here since the folders menu item is not part of the original menu
        key={rail.id}
        className="no-background"
        isLoadingAssets={rail.loadingAssets}
        railName={rail.title}
        assets={rail.assets}
        channelId={rail.channelId}
        hasMore={rail.hasMore}
        onCardClick={onCardClick}
        onHeaderClick={onHeaderClick}
      />
    );
  });
};

const getMessage = (option: FolderPurchaseOption) => {
  let line1 = '';
  if (OptionUtils.isBundleOption(option)) {
    line1 =
      option?.purchaseOptionType === PurchaseOptionType.BUY
        ? translate['modal.purchaseRentalSuccessMessage3']
        : translate['modal.purchaseRentalSuccessMessage1'];
  } else if (OptionUtils.isSubscriptionOption(option)) {
    line1 = translate['modal.subscriptionSuccess'];
  }
  const line2 = option.asset?.title;
  return `${line1}\n${line2}`;
};

export default function MainPage(): JSX.Element {
  const [showMoreInfo, setShowMoreInfo] = useState(false);
  const [optionButtons, setOptionButtons] = useState<(ActionButton | AssetActionButton)[]>([]);
  const [errorMessage, setErrorMessage] = useState('');
  const [showResultModal, setShowResultModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);

  const dispatch = useDispatch();
  const location = useLocationWithState();
  const history = useHistoryWithState();
  const router = useRouter();

  const { assetId } = useParams<PathParams>();
  const { entryPoint } = router.queryParams;

  const { entryAsset, parentContext } = (location.state as LocationState | undefined) || {};

  const ctxRef = useRef<FolderInfoContext>({
    entryAsset,
    entryAssetId: entryAsset?.id || Number(assetId),
    entryPoint,
  });

  const rails = useSelector((state: StoreState) => Selectors.rails.getRails(state, ctxRef.current));
  const isRailsProcessing = useSelector((state: StoreState) => Selectors.rails.isProcessing(state, ctxRef.current));
  const asset = useSelector((state: StoreState) => Selectors.folder.getAsset(state, ctxRef.current));
  const isAssetProcessing = useSelector((state: StoreState) => Selectors.folder.isProcessing(state, ctxRef.current));
  const assetError = useSelector((state: StoreState) => Selectors.folder.error(state, ctxRef.current));
  const folderOptions = useSelector((state: StoreState) => Selectors.options.getOptions(state));

  useEffect(() => {
    const ctx = ctxRef.current;
    dispatch(ActionCreators.init(ctx, parentContext));

    window.scroll({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });
  }, []);

  useEffect(() => {
    if (!assetError) {
      return;
    }

    const section = router.getSection();

    switch (section) {
      case MainSection.MY_TV:
        history.replace(PrivateRoutePath.MYTV);
        break;
      case MainSection.WATCH_TV:
        history.replace(PrivateRoutePath.WATCHTV);
        break;
      default:
        history.replace(PrivateRoutePath.ONDEMAND);
    }
  }, [assetError, history, router]);

  const onActionButtonClick = (actionButtonkey: ActionButtonKey) => {
    switch (actionButtonkey) {
      case ActionButtonKey.MORE_INFO:
        setShowMoreInfo(true);
        break;
      default:
        break;
    }
  };

  const folder = entryAsset || asset;
  const backDetails = RouterUtils.getBackDetails(router.getSection(), ctxRef.current, parentContext);

  // PURCHASES

  const purchaseError = useSelector((state: StoreState) => Selectors.runOption.getError(state));
  const purchaseSuccess = useSelector((state: StoreState) => Selectors.runOption.isSuccess(state));
  const defaultUser = useSelector(Ducks.Household.Selectors.data.getDefaultUser);
  const [purchaseSuccessMessage, setPurchaseSuccessMessage] = useState('');
  const [purchaseOption, setPurchaseOption] = useState<FolderPurchaseOption | undefined>();
  const [showPurchasePinModal, setShowPurchasePinModal] = useState(false);

  useEffect(() => {
    if (purchaseOption && purchaseError) {
      setErrorMessage(purchaseError.translation);
      setShowResultModal(false);
      setShowErrorModal(true);
      addErrorEventByEntryPoint(purchaseError, ctxRef.current.entryPoint);
    }
  }, [purchaseError, purchaseOption]);

  useEffect(() => {
    if (folder && purchaseSuccess) {
      if (purchaseOption) {
        setPurchaseSuccessMessage(getMessage(purchaseOption));
      }
      dispatch(ActionCreators.options.request(folder));
    }
  }, [dispatch, folder, purchaseOption, purchaseSuccess]);

  const continueWithPurchase = useCallback(
    (option: AssetOption) => {
      setShowResultModal(true);
      if (OptionUtils.isFolderPurchaseOption(option)) {
        setPurchaseOption(option);
        const { productPrice } = option;
        const productId = NumberUtils.parse(productPrice.productId);
        if (productId !== undefined) {
          dispatch(ActionCreators.runOption.request(option));
        }
      }
    },
    [dispatch]
  );

  const purchase = useCallback(
    (option: AssetOption) => {
      if (OptionUtils.isFolderPurchaseOption(option)) {
        setPurchaseOption(option);
        if (defaultUser?.parentalValidation && defaultUser?.purchasePinEnabled) {
          dispatch(ManagePin.ActionCreators.pinReset());
          setShowPurchasePinModal(true);
        } else {
          continueWithPurchase(option);
        }
      }
    },
    [continueWithPurchase, defaultUser?.parentalValidation, defaultUser?.purchasePinEnabled, dispatch]
  );

  const onPurchasePinModalCancel = () => {
    setShowPurchasePinModal(false);
    dispatch(ManagePin.ActionCreators.pinReset());
  };

  const onPurchasePinConfirmation = () => {
    setShowPurchasePinModal(false);
    dispatch(ManagePin.ActionCreators.pinReset());
    if (purchaseOption) {
      continueWithPurchase(purchaseOption);
    }
  };

  // OPTIONS

  useEffect(() => {
    if (folder) {
      dispatch(ActionCreators.options.request(folder));
    }
  }, [dispatch, folder]);

  const getAssetActionButtons = (
    options: AssetOption[],
    actionButtonCallback: (option: AssetOption) => void
  ): AssetActionButton[] => {
    return options.map((option) => {
      if (OptionUtils.isSubscriptionOption(option)) {
        if (
          option.productPrice.status === ProductPriceStatus.FREE ||
          option.productPrice.status === ProductPriceStatus.PURCHASED
        ) {
          return {
            ...option,
            className: 'active disabled opaque',
          };
        }
      }
      return {
        ...option,
        className: 'opaque',
        onClick: () => actionButtonCallback(option),
      };
    });
  };

  useEffect(() => {
    if (folderOptions) {
      const subscribeButtons = getAssetActionButtons(folderOptions, (option: AssetOption) => {
        purchase(option);
      });
      const otherButtons = getHeroActionButtons(folder, onActionButtonClick) || [];
      setOptionButtons([...subscribeButtons, ...otherButtons]);
    }
  }, [folder, folderOptions, purchase]);

  if (isAssetProcessing) {
    return (
      <div className="layout-spinner-container">
        <Spinner isActive />
      </div>
    );
  }

  const onCardClick = (assetClicked: Asset) => {
    router.push({ asset: assetClicked, entryPoint });
  };

  const onHeaderClick = (_itemId: number | string, channelId?: number) => {
    router.push({
      routePath: PrivateRoutePath.FOLDER_INFO_GRID,
      valuesSeries: [ctxRef.current.entryAssetId, channelId],
      state: { entryFolder: folder },
      entryPoint,
    });
  };

  const onErrorDismissed = () => {
    setErrorMessage('');
    setShowErrorModal(false);
  };

  return (
    <div id="FolderMainPage_container" className="folder">
      <Modal isShowing={showPurchasePinModal}>
        <Pin closeModal={onPurchasePinModalCancel} onPinCallback={onPurchasePinConfirmation} />
      </Modal>

      <Modal
        title={purchaseSuccess ? translate['modal.title.success'] : translate['modal.title.processing']}
        message={purchaseSuccessMessage}
        setIsShowing={setShowResultModal}
        isShowing={errorMessage === '' && showResultModal}
        buttons={[{ id: 'close', text: translate['modal.button.close'], onClick: () => setShowResultModal(false) }]}
      />

      <Modal
        title={translate['modal.title.error']}
        message={errorMessage}
        buttons={[{ id: 'ok', text: translate.modalButtonOk, onClick: () => onErrorDismissed() }]}
        isShowing={errorMessage !== '' && showErrorModal}
        setIsShowing={() => onErrorDismissed()}
      />

      <div id="FolderMainPage_hero" className="hero">
        <Hero
          className="hero-container"
          logo={folder?.logo?.color?.uri}
          backgroundImage={folder?.backdrop?.uri}
          backgroundVideo={folder?.bckVideo}
          backLabel={backDetails.backLabel}
          backUrl={backDetails.backUrl}
          title={folder?.title}
          description={folder?.description}
          actionButtons={optionButtons}
        />
      </div>

      {showMoreInfo && folder?.moreInfo ? (
        <MoreInfo title={folder?.title || ''} info={folder?.moreInfo} onClose={() => setShowMoreInfo(false)} />
      ) : null}

      <div className="content-container">
        {!isRailsProcessing ? (
          renderRails(rails, onCardClick, onHeaderClick)
        ) : (
          <div className="layout-spinner-container">
            <Spinner className="folder-spinner" isActive isEmbedded />
          </div>
        )}
      </div>
    </div>
  );
}
